import { useSelector } from 'react-redux';
import { Text, Col, FlexboxGrid } from 'rsuite';
import { getUsersActionableTaxPaymentPeriodsAsync } from '../../../store/slices/dashboard/dashboardThunks';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  selectConfigExpressBulkRenewEnabled,
  selectConfigExpressRegistrationRenewEnabled,
  selectConfigExpressRegistrationVerbiage,
  selectConfigExpressTaxEnabled,
} from '../../../store/slices/config/configSelectors';
import {
  selectUsersTaxableLicensePeriods,
  selectUsersTaxableLicensePeriodsInProgress,
  selectUsersTaxableLicensePeriodsSuccess,
} from '../../../store/slices/dashboard/dashboardSelectors';
import first from 'lodash/first';
import dayjs from 'dayjs';
import { BREAKPOINT_ENUM, DASHBOARD_ACTIONS_ENUM } from '../../../common/enums';
import ActionItem from './actionItem';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import IconIndicator from '../../user-feedback/IconIndicator';
import Notice from '../../user-feedback/Notice';
import debounce from 'lodash.debounce';
import { getAddressWithUnitNumber } from '../../../common/utils';
import { useAppDispatch } from 'src/store/store';

// Pass in  a ref to the usersLicenseWrapper component
// The height if this component should match the users license component
const ActionsList = ({
  renewableLicensesData,
  usersLicensesWrapperRef,
  forceRerender = 0,
}) => {
  const dispatch = useAppDispatch();

  const [scrollHeight, setScrollHeight] = useState('auto');
  const [scrollHeightEmpty, setScrollHeightEmpty] = useState('auto');
  const [containerHeight, setContainerHeight] = useState('auto');

  const renewEnabled = useSelector(selectConfigExpressRegistrationRenewEnabled);
  const bulkRenewEnabled = useSelector(selectConfigExpressBulkRenewEnabled);
  const {
    data: renewableLicenses,
    loading: renewableLicensesInProgress,
    error: renewableLicensesError,
  } = renewableLicensesData;

  const taxEnabled = useSelector(selectConfigExpressTaxEnabled);
  const taxablePeriods = useSelector(selectUsersTaxableLicensePeriods);
  const taxablePeriodsSuccess = useSelector(
    selectUsersTaxableLicensePeriodsSuccess
  );
  const taxablePeriodsInProgress = useSelector(
    selectUsersTaxableLicensePeriodsInProgress
  );
  const registrationVerbiage = useSelector(
    selectConfigExpressRegistrationVerbiage
  );

  const headingRef = useRef(null);
  const columnHeadingRef = useRef(null);

  useEffect(() => {
    if (taxEnabled) {
      dispatch(getUsersActionableTaxPaymentPeriodsAsync());
    }
  }, [dispatch, taxEnabled]);

  const generateTaxActions = useCallback(() => {
    return taxablePeriods?.map((period) => {
      let label = `Pay Tax (${period.name}) for ${period.licenses.length} properties`;

      let selectedLicense = null;

      // if there is only one actionable permit for the period set the selctedLicense, so that onClick we go to the report revenue page rather than the select properties page
      if (period.licenses.length <= 1) {
        const license = first(period.licenses);
        const address = getAddressWithUnitNumber(
          license.address,
          license.unitNumber
        );
        label = `Pay Tax (${period.name}) ${address}`;
        selectedLicense = {
          address: address,
          licenseNumber: license.license,
        };
      }

      return {
        label: label,
        startDate: period.startDate,
        endDate: period.endDate,
        dueDate: period.dueDate,
        type: DASHBOARD_ACTIONS_ENUM.BULK_TAX,
        selectedLicense: selectedLicense,
        periodLabel: period.name,
      };
    });
  }, [taxablePeriods]);

  const generateRenewalActions = useCallback(() => {
    return renewableLicenses?.map((license) => {
      const fullAddress = getAddressWithUnitNumber(
        license.property_address,
        license.unit_number
      );
      const label = `Renew ${registrationVerbiage} ${fullAddress}`;

      return {
        label: label,
        dueDate: license.expiration_date,
        selectedLicense: {
          address: fullAddress,
          licenseNumber: license.license,
          applicationNumber: license.application_number,
        },
        type:
          bulkRenewEnabled && renewableLicenses.length > 1
            ? DASHBOARD_ACTIONS_ENUM.BULK_RENEW
            : DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW,
      };
    });
  }, [renewableLicenses, registrationVerbiage, bulkRenewEnabled]);

  const taxActions = useMemo(() => {
    if (taxEnabled && taxablePeriods) {
      return generateTaxActions();
    }
    return [];
  }, [taxEnabled, taxablePeriods, generateTaxActions]);

  const renewalActions = useMemo(() => {
    if (renewEnabled && renewableLicenses) {
      return generateRenewalActions();
    }
    return [];
  }, [renewEnabled, renewableLicenses, generateRenewalActions]);

  const actions = useMemo(() => {
    const allActions = [...taxActions, ...renewalActions];

    //order by due date desc
    return sortBy(allActions, ({ dueDate }) => dayjs(dueDate).unix());
  }, [
    taxEnabled,
    renewEnabled,
    taxablePeriods,
    renewEnabled,
    generateTaxActions,
    generateRenewalActions,
  ]);

  const isLoading = useMemo(() => {
    const loadingRenewals = renewEnabled && renewableLicensesInProgress;
    const loadingTax = taxEnabled && taxablePeriodsInProgress;

    return loadingRenewals || loadingTax;
  }, [
    renewEnabled,
    renewableLicensesInProgress,
    taxEnabled,
    taxablePeriodsInProgress,
  ]);

  const updateHeight = debounce(() => {
    // On small screens we don't want the height based on the usersLicenses component height
    if (window.innerWidth < BREAKPOINT_ENUM.MEDIUM) {
      setScrollHeight('auto');
      setContainerHeight('auto');
      setScrollHeightEmpty('auto');
    } else {
      // -8 to take into account the padding
      setScrollHeight(
        usersLicensesWrapperRef?.current?.clientHeight -
          headingRef?.current?.clientHeight -
          (columnHeadingRef?.current?.clientHeight || 0) -
          10
      );
      setContainerHeight(usersLicensesWrapperRef?.current?.clientHeight);
      setScrollHeightEmpty(
        usersLicensesWrapperRef?.current?.clientHeight -
          headingRef?.current?.clientHeight -
          8
      );
    }
  }, 100);

  // set corret height on page load weh the refs change
  useEffect(() => {
    if (usersLicensesWrapperRef?.current && headingRef?.current) {
      updateHeight();
    }
  }, [usersLicensesWrapperRef, headingRef, forceRerender]);

  // Add re-size listeners so that we can adjust the height of this component based on the height of the users license component
  useEffect(() => {
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [usersLicensesWrapperRef, updateHeight]);

  const ActionableItemsContent = useCallback(() => {
    if (isEmpty(actions)) {
      if (isLoading) return <IconIndicator className="pv-24 pv-48" />;

      const isRenewalsEvaluationSucceeded =
        !renewEnabled || renewableLicensesError === undefined;
      const isTaxEvaluationSucceeded = !taxEnabled || taxablePeriodsSuccess;

      if (isRenewalsEvaluationSucceeded && isTaxEvaluationSucceeded) {
        return (
          <FlexboxGrid.Item
            role="row"
            className="ph-24 pv-48"
            colspan={24}
            align="middle"
          >
            <div role="cell">
              <Text as="i">No outstanding actions.</Text>
            </div>
          </FlexboxGrid.Item>
        );
      } else {
        return (
          <FlexboxGrid.Item
            className="ph-24 pv-48"
            role="row"
            colspan={24}
          >
            <Notice message="Failed to retrieve data."></Notice>
          </FlexboxGrid.Item>
        );
      }
    } else {
      return (
        <FlexboxGrid.Item
          role="row"
          className="ph-0 pv-0"
          colspan={24}
          align="middle"
        >
          <div role="cell">
            <div role="grid">
              <FlexboxGrid
                align="middle"
                role="row"
              >
                <FlexboxGrid.Item
                  align="start"
                  style={{ borderTopLeftRadius: '4px' }}
                  className="pl-24 pv-8 actions-panel-header-container"
                  as={Col}
                  colspan={24}
                  lg={16}
                  xsHidden
                  smHidden
                  mdHidden
                  ref={columnHeadingRef}
                >
                  <Text>Action</Text>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  align="start"
                  className="pv-8 actions-panel-header-container"
                  as={Col}
                  colspan={4}
                  xsHidden
                  smHidden
                  mdHidden
                >
                  <Text>Due by</Text>
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  align="start"
                  style={{ borderTopRightRadius: '4px', height: '100%' }}
                  className="pr-24 pv-8 actions-panel-header-container"
                  as={Col}
                  colspan={4}
                  xsHidden
                  smHidden
                  mdHidden
                >
                  &nbsp;
                </FlexboxGrid.Item>
                <FlexboxGrid.Item
                  align="start"
                  as={Col}
                  colspan={24}
                  className="grdGreyStripe scroll-bar ph-0"
                  style={{ overflowY: 'auto', height: scrollHeight }}
                >
                  {actions?.map((action, index, arr) => {
                    const isBulkRenewRendered =
                      arr.findIndex((i) => i.type === 'bulkRenew') !== index;

                    // Don't render bulk renew row if it's already rendered
                    if (action.type === 'bulkRenew' && isBulkRenewRendered)
                      return;

                    // Handle bulk renew row rendering
                    if (!isBulkRenewRendered) {
                      const bulkRenewArr = arr.filter(
                        (a) => a.type === 'bulkRenew'
                      );
                      const bulkRenewAction = {
                        type: 'bulkRenew',
                        label: `${action.label} + ${bulkRenewArr.length - 1} properties`,
                        dueDate: action.dueDate,
                      };
                      return (
                        <ActionItem
                          key={`action_item_bulk_renew`}
                          action={bulkRenewAction}
                        />
                      );
                    }

                    // Handle rendering all other types of row
                    return (
                      <ActionItem
                        key={`action_item_${action.type}_${index}`}
                        action={action}
                      />
                    );
                  })}
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </div>
          </div>
        </FlexboxGrid.Item>
      );
    }
  }, [
    actions,
    isLoading,
    renewEnabled,
    taxEnabled,
    taxablePeriodsSuccess,
    renewableLicensesError,
    scrollHeight,
  ]);

  return (
    <FlexboxGrid
      role="grid"
      style={{ height: containerHeight }}
    >
      <FlexboxGrid.Item
        role="row"
        ref={headingRef}
        colspan={24}
        xs={24}
        className="mb-8"
      >
        <div role="cell">
          <Text
            as="h2"
            className="text-large text-bold"
          >
            Actions Required
          </Text>
        </div>
      </FlexboxGrid.Item>

      <FlexboxGrid.Item
        role="row"
        style={{ height: scrollHeightEmpty }}
        colspan={24}
        className="content-section-no-padding"
      >
        <div role="cell">
          <div role="grid">
            <ActionableItemsContent />
          </div>
        </div>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default ActionsList;
